import { useEffect, useState } from 'react';
import { useParams, useLoaderData } from 'react-router-dom';
import { redirectErrorPage } from '../../utils/errors';
import ChartComponent from '../../components/ChartComponent';
import { useTranslation } from 'react-i18next';
import Intro from '../../components/common/Intro/Intro';
import { capitalizeFirstLetter } from '../../utils/texts';
import SummarySection from '../../components/common/SummarySection/SummarySection';
import Section from '../../components/common/Section/Section';
import { Colors } from '../../Constants';
import MemberCard from '../../components/common/MemberCard/MemberCard';
import axios from 'axios';
import { useVoteColors } from '../../utils/hooks';
import { MemberDetailResponseType } from '../../types';
import { PartyDetailResponseType } from '../../types/PartyDetailResponseType';

function PartyDetail() {
    const { t } = useTranslation();
    const { bodyType, municipalitaId, zastupitelstvoId } = useParams();
    const partyData = useLoaderData() as PartyDetailResponseType;
    const [inactiveMembersWithExtraData, setInactiveMembersWithExtraData] = useState<MemberDetailResponseType[]>();
    const activeMembers = partyData.clenove.filter((member) => member.aktivni);

    const voteColors = useVoteColors();
    //TODO: z DB
    const voteOptions = { ano: 1, ne: 2, 'zdržel se': 3, nehlasoval: 4, 'nepřít.': 5, omluven: 6, tajná: 7 };

    // TODO: edit this page's endpoint to avoid extra fetches, at the moment its required to fetch extra data about inactive members (new party, inactive status, ..)
    useEffect(() => {
        const getMemberDataById = async (id: number) => {
            try {
                const response = await axios.get(`${municipalitaId}/${bodyType}/${zastupitelstvoId}/member/${id}`);
                return response.data;
            } catch (error) {
                console.error('Error fetching member data:', error);
                return null;
            }
        };

        const fetchInactiveMembersData = async () => {
            const inactiveMembers = partyData.clenove.filter((member) => !member.aktivni);
            const memberDataPromises = inactiveMembers.map((member) => getMemberDataById(member.id));
            const membersData = await Promise.all(memberDataPromises);
            setInactiveMembersWithExtraData(membersData);
        };

        fetchInactiveMembersData();
    }, [partyData, municipalitaId, bodyType, zastupitelstvoId]);

    if (partyData !== undefined) {
        if (JSON.stringify(partyData) === '{"error":"neexistujici ID"}') {
            redirectErrorPage(municipalitaId, zastupitelstvoId);
        } else {
            let summaryVotesLabels = Object.keys(partyData.celkove_hlasy);
            let summaryVotesValues = Object.values(partyData.celkove_hlasy);

            let summaryVotesData = {
                labels: [''],
                datasets: summaryVotesValues
                    .map((item, index) => ({
                        type: 'bar',
                        label: capitalizeFirstLetter(summaryVotesLabels[index]),
                        backgroundColor: voteColors[summaryVotesLabels[index] as keyof typeof voteColors],
                        data: [item]
                    }))
                    .sort((a, b) => {
                        return Object.keys(voteOptions).indexOf(a.label) - Object.keys(voteOptions).indexOf(b.label);
                    })
            };

            const stackedOptions = {
                indexAxis: 'y',
                maintainAspectRatio: false,
                aspectRatio: 0.8,
                plugins: {
                    tooltips: {
                        mode: 'index',
                        intersect: false
                    },
                    legend: {
                        labels: {
                            generateLabels: (chart: { data: { datasets: any } }) => {
                                const datasets = chart.data.datasets;
                                return datasets.map(
                                    (data: { label: any; data: any[]; backgroundColor: any }, i: any) => ({
                                        text: `${data.label} (${data.data[0]})`,
                                        fillStyle: data.backgroundColor,
                                        strokeStyle: 'white',
                                        datasetIndex: 0,
                                        index: i
                                    })
                                );
                            }
                        },
                        onHover: function (
                            evt: { native: { target: { style: { cursor: string } } } },
                            item: { index: any },
                            legend: { chart: { tooltip: any; data: { datasets: any[] }; update: () => void } }
                        ) {
                            const tooltip = legend.chart.tooltip;
                            if (tooltip.getActiveElements().length > 0) {
                                tooltip.setActiveElements([], { x: 0, y: 0 });
                            }
                            tooltip.setActiveElements([{ datasetIndex: item.index, index: 0 }], { x: 0, y: 0 });

                            evt.native.target.style.cursor = 'pointer';
                            legend.chart.data.datasets.forEach((i: { backgroundColor: string | any[] }, id: any) => {
                                if (id !== item.index && i.backgroundColor.length === 7) {
                                    i.backgroundColor += '30';
                                }
                            });
                            legend.chart.update();
                        },
                        onLeave: function (
                            evt: any,
                            item: { index: any },
                            legend: { chart: { data: { datasets: any[] }; update: () => void } }
                        ) {
                            legend.chart.data.datasets.forEach((i: { backgroundColor: string | any[] }, id: any) => {
                                if (id !== item.index && i.backgroundColor.length > 7) {
                                    i.backgroundColor = i.backgroundColor.slice(0, -2);
                                }
                            });
                            legend.chart.update();
                        }
                    }
                },
                scales: {
                    x: {
                        stacked: true
                    },
                    y: {
                        stacked: true,
                        position: 'right'
                    }
                },
                onHover: function (evt: any, item: any) {
                    evt.native.target.style.cursor = 'default';
                }
            };

            const partyIsActiveWholeTerm = partyData.dochazka.mandat === partyData.dochazka.celkem_zasedani;
            const allMembersCountString =
                partyData.clenove.length > partyData.pocet_aktivnich
                    ? `(${capitalizeFirstLetter(t('whole_term'))} ${partyData?.clenove.length} ${t('councilMember', {
                          count: partyData.clenove.length
                      })})`
                    : '';
            const votingPercentageAttendanceString = `${partyData.attendance.toFixed(2)}%`;

            const summarySectionItems = [
                {
                    icon: 'description',
                    text: partyIsActiveWholeTerm
                        ? `${capitalizeFirstLetter(t('active'))} ${t('whole')} ${t('term')}`
                        : `${capitalizeFirstLetter(t('mandate'))} ${partyData.dochazka.mandat} ${t('of')} ${
                              partyData.dochazka.celkem_zasedani
                          } ${t('meetings')}`,

                    highlightSubString: partyIsActiveWholeTerm
                        ? t('whole')
                        : `${partyData.dochazka.mandat} ${t('of')} ${partyData.dochazka.celkem_zasedani}`
                },
                {
                    icon: 'group',
                    text: `${partyData.pocet_aktivnich} ${t('mandate', { count: partyData.pocet_aktivnich })}
                    ${allMembersCountString}`,
                    highlightSubString: `${partyData.pocet_aktivnich}`,
                    mutedSubString: allMembersCountString
                },
                {
                    icon: 'check_box',
                    text: `${votingPercentageAttendanceString} ${t('voting-attendance')}`,
                    highlightSubString: votingPercentageAttendanceString
                }
            ];

            return (
                <>
                    <Intro
                        title={partyData?.strana_zkratka}
                        titleColor={partyData?.strana_barva}
                        subtitle={
                            partyData?.pocet_aktivnich === 0
                                ? capitalizeFirstLetter(t('inactive_party'))
                                : partyData?.koalicni_strana
                                ? capitalizeFirstLetter(t('coalition_party'))
                                : capitalizeFirstLetter(t('opposition_party'))
                        }
                    />
                    <SummarySection summaryItems={summarySectionItems} />
                    <Section title={t('Members')}>
                        {activeMembers.length > 0 && (
                            <>
                                <h4>{t('active-members')}</h4>
                                <div className='member-card-list'>
                                    {activeMembers.map((member) => {
                                        const nameParts = member.jmeno.split(' ');
                                        const firstName = nameParts[0];
                                        const lastName = nameParts.slice(1).join(' ');

                                        return (
                                            <MemberCard
                                                key={member.id}
                                                memberData={{
                                                    id: member.id,
                                                    firstName: firstName,
                                                    lastName: lastName,
                                                    gender: member.pohlavi
                                                }}
                                                partyData={{
                                                    partyName: partyData.strana_zkratka,
                                                    partyColor: partyData.strana_barva
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            </>
                        )}
                        {inactiveMembersWithExtraData && inactiveMembersWithExtraData.length > 0 && (
                            <>
                                <h4 style={{ color: Colors.darkGray, marginTop: '24px' }}>{t('history')}</h4>
                                <div className='member-card-list'>
                                    {/* // TODO: Move inactive / active in other party to MemberCard component */}
                                    {inactiveMembersWithExtraData?.map((member) => {
                                        const nameParts = member.jmeno.split(' ');
                                        const firstName = nameParts[0];
                                        const lastName = nameParts.slice(1).join(' ');

                                        const newParty = member.politicke_strany.find(
                                            (party) => party.datum_do === null
                                        );

                                        return (
                                            <MemberCard
                                                key={member.id}
                                                memberData={{
                                                    id: member.id,
                                                    firstName: firstName,
                                                    lastName: lastName,
                                                    gender: member.pohlavi,
                                                    inactive: !member.aktivni_clen
                                                }}
                                                partyData={{
                                                    partyName: newParty
                                                        ? `${capitalizeFirstLetter(t('now_for'))} ${newParty.zkratka}`
                                                        : partyData.strana_zkratka,
                                                    partyColor: newParty ? newParty.strana_barva : Colors.darkGray
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </Section>
                    <Section title={t('votings-summary')}>
                        <ChartComponent
                            type='bar'
                            data={summaryVotesData}
                            options={stackedOptions}
                            height={'200px'}
                            width='100%'
                            xTitle={undefined}
                            yTitle={undefined}
                            aria={undefined}
                        />
                    </Section>
                </>
            );
        }
    }
}

export default PartyDetail;
